import React, { useState } from "react";
import { BookOpen, FileDown, Share2, Copy, LogIn, LayoutDashboard, ClipboardList, Users, Pill, Bed, Sparkles, Hospital, Bell, Wallet, Landmark, Scale, TrendingDown, Building2, Percent, ChevronRight, Target, CalendarClock, Activity, StickyNote, HeartPulse, Info } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { exportGuida, exportGuidaIllustrata } from "../lib/exporter";
import { toast } from "sonner";

const IMG = (n) => `/guida/cat/${n}.png`;

// ─────────── Sezioni introduttive (per tab) ───────────
const introOperatore = [
  {
    id: "a-cosa-serve", titolo: "1 · A cosa serve il software", icon: Target,
    testo: "È il registro digitale unico della struttura: raccoglie ogni giorno tutto ciò che riguarda gli ospiti e il lavoro degli operatori (presenze, terapie, alvo, alzate e messa a letto, pulizie, consegne tra turni, uscite e passaggi in ospedale).",
    punti: [
      "Sostituisce quaderni e fogli sparsi con un unico archivio condiviso e sempre aggiornato.",
      "Tutto ciò che scrivi resta salvato con data, ora e firma, ed è visibile ai colleghi dei turni successivi.",
      "L'obiettivo non è controllare chi lavora, ma garantire all'ospite continuità di cura e sicurezza.",
      "In caso di controlli, emergenze o dubbi medici, lo storico scritto protegge l'ospite e protegge te.",
    ],
  },
  {
    id: "primo-accesso", titolo: "2 · Primo accesso", icon: LogIn,
    testo: "L'amministratore ti consegna in privato email e password personali; il PIN (2 o 4 cifre) serve per firmare rapidamente le operazioni dal tablet.",
    steps: [
      "Apri il link del software nel browser.",
      "Inserisci la tua email e la password ricevute (l'occhiolino mostra o nasconde ciò che scrivi).",
      "Quando richiesto, digita il tuo PIN personale.",
      "In alto scegli la struttura in cui stai lavorando: tutti i dati si riferiranno a quella.",
      "Se il PIN non funziona o l'hai dimenticato, chiedi all'amministratore di reimpostarlo (non usare quello di un collega).",
    ],
    img: IMG("dashboard"), imgCaption: "Dopo l'accesso vedi la Dashboard: in alto il selettore struttura e il tuo profilo, a sinistra il menu.",
  },
  {
    id: "schermata", titolo: "3 · Com'è fatta la schermata", icon: LayoutDashboard,
    testo: "La pagina è divisa in tre zone fisse. A queste si aggiungono, al centro, i box informativi e i grafici.",
    punti: [
      "A sinistra il menu con tutte le voci, raggruppate per area (Operativa, Tecnica, Amministrazione). Le voci che non vedi non sono abilitate per te: è normale.",
      "In alto la barra con il selettore di struttura, la campanella delle notifiche e il tuo profilo.",
      "Al centro il contenuto: i box KPI colorati (es. Ospiti, Presenti, In ospedale) danno il colpo d'occhio, sotto ci sono le schede operative (turni di oggi, menù, consegne).",
      "Su telefono il menu si apre con l'icona in alto a sinistra e in basso compare una barra di navigazione rapida.",
    ],
    img: IMG("dashboard"), imgCaption: "I box in alto sono i KPI (numeri chiave); sotto le schede con turni, menù e attività del giorno.",
  },
  {
    id: "giornata", titolo: "4 · L'iter di lavoro: la giornata tipo", icon: CalendarClock,
    testo: "Il flusso della giornata segue sempre lo stesso giro. Regola d'oro: compila nel momento in cui fai la cosa, non a fine turno a memoria — è lì che nascono le dimenticanze.",
    steps: [
      "Inizio turno: leggi le CONSEGNE del turno precedente e le NOTE / prese visione.",
      "Registra le PRESENZE (tue e del turno).",
      "Durante il turno: somministra e spunta le TERAPIE previste.",
      "Segna l'ALVO intestinale di ogni ospite.",
      "Registra ALZATE e MESSA A LETTO con l'orario.",
      "Spunta le PULIZIE degli ambienti assegnati.",
      "Se un ospite esce/rientra o va in OSPEDALE, registralo subito.",
      "Fine turno: controlla di aver compilato tutto e lascia le CONSEGNE per chi arriva dopo di te.",
    ],
  },
];

const introAdmin = [
  {
    id: "a-cosa-serve-adm", titolo: "1 · A cosa serve (lato gestione)", icon: Target,
    testo: "Oltre alla parte clinica/operativa, il software gestisce tutta l'economia della struttura: rette incassate, spese, personale, mutui, ammortamenti e la stima delle imposte, con l'isolamento dei dati per azienda e per struttura.",
    punti: [
      "«Ricavi − Costi» è la gestione reale (cassa): incassi meno spese e personale, a costi pieni.",
      "«Risultati & Tasse» è la vista fiscale: applica le rettifiche (spese capitalizzate, quota indeducibile), toglie interessi e ammortamenti e stima le imposte.",
      "Sono due verità diverse ma entrambe corrette: non vanno confuse.",
      "Un bene sopra 516,46 € va capitalizzato (pesa come quota di ammortamento negli anni); sotto quella soglia si deduce interamente nell'anno.",
    ],
  },
  {
    id: "primo-accesso-adm", titolo: "2 · Primo accesso e permessi", icon: LogIn,
    testo: "L'amministratore accede con email e password. Da «Utenti e permessi» crea gli operatori e decide cosa ciascuno può vedere.",
    steps: [
      "Accedi con le tue credenziali di amministratore.",
      "Apri «Utenti e permessi» per creare operatori con email, password e PIN.",
      "Assegna i permessi: l'operatore vedrà solo i moduli abilitati.",
      "Consegna le credenziali di persona o in un messaggio privato (mai in un'email di gruppo).",
      "Se serve, reimposta password e PIN di un operatore con l'icona chiave.",
    ],
    img: IMG("utenti"), imgCaption: "Utenti e permessi: account di accesso, operatori collegati e reset credenziali.",
  },
  {
    id: "schermata-adm", titolo: "3 · Com'è fatta la schermata (con grafici)", icon: LayoutDashboard,
    testo: "La Dashboard amministrativa riassume l'economia dell'azienda con box KPI e grafici. In alto i saldi delle banche e il selettore anno.",
    punti: [
      "Box economici colorati: ricavi, costi, risultato di gestione e situazione fiscale.",
      "Grafici circolari per mutui e cespiti (ammortamenti) e la ripartizione delle utenze.",
      "Un selettore a 12 mesi apre la tabella completa fino alla cassa effettiva.",
      "Come nel resto dell'app, a sinistra il menu e in alto il selettore di struttura.",
    ],
    img: IMG("dash-admin"), imgCaption: "Dashboard amministrativa: KPI in alto, grafici circolari e tabella annuale.",
  },
  {
    id: "giornata-adm", titolo: "4 · L'iter di lavoro amministrativo", icon: CalendarClock,
    testo: "Il giro tipico del mese per tenere i conti allineati.",
    steps: [
      "Registra le RETTE incassate del mese (Ricavi e rette).",
      "Inserisci le SPESE per categoria, segnando IVA/ritenuta (F24) e trattamento fiscale.",
      "Aggiorna BUSTE PAGA e i relativi F24 del personale.",
      "Controlla MUTUI (rate pagate) e AMMORTAMENTI dei cespiti.",
      "Verifica «Ricavi − Costi» per il risultato di cassa e «Risultati & Tasse» per la stima imposte.",
      "Aggiorna i saldi in BANCHE e leggi il quadro completo nella Dashboard amministrativa.",
    ],
  },
];

// ─────────── Categorie con screenshot ───────────
const catOperatore = [
  { titolo: "Gestione Ospiti", icon: Users, img: IMG("ospiti"),
    come: "L'elenco degli ospiti della struttura, mese per mese. In alto i KPI (ospiti totali, % occupazione, posti) e il pulsante «Nuovo ospite».",
    steps: ["Scegli la struttura in alto e il mese.", "Usa «Nuovo ospite» per inserire un ospite.", "Su ogni riga: apri la scheda, modifica, dimetti o elimina.", "Il badge di stato mostra dimessi/riammessi del mese."] },
  { titolo: "Scheda dell'ospite", icon: Bed, img: IMG("scheda-ospite"),
    come: "Tutta la storia dell'ospite in un unico posto: anagrafica, dati sanitari, terapie, diario/note, movimenti e documenti. Si apre toccando il nome nella lista Ospiti.",
    steps: ["Tieni aggiornate le note giornaliere quando noti qualcosa di rilevante.", "Controlla le terapie prima di somministrare.", "Segnala subito cadute, febbre o rifiuto del cibo nelle note e nelle consegne.", "Non modificare dati anagrafici/sanitari se non autorizzato: avvisa il responsabile."] },
  { titolo: "Operatori", icon: Users, img: IMG("operatori"),
    come: "L'elenco degli operatori della struttura con ruolo, contatti e stato (attivo/cessato). Da qui apri la scheda personale di ognuno.",
    steps: ["Tocca un operatore per aprire la sua scheda.", "Verifica ruolo e recapiti.", "Gli operatori cessati restano nello storico dei mesi passati."] },
  { titolo: "Scheda operatore e PIN", icon: Users, img: IMG("scheda-operatore"),
    come: "La scheda personale: identifica chi sei nel sistema, con PIN per la firma rapida, permessi, turni e presenze collegati. Il PIN è personale e non va condiviso.",
    steps: ["Molte operazioni chiedono il PIN: certifica che le hai fatte TU.", "Se il PIN non funziona, chiedi all'amministratore di reimpostarlo.", "Nella scheda trovi le tue firme, i richiami e i turni."] },
  { titolo: "Turni", icon: CalendarClock, img: IMG("turni"),
    come: "Il calendario dei turni del mese: chi è di turno e quando. Le lettere/colori indicano la fascia (M mattina, P pomeriggio, N notte, C cucina).",
    steps: ["Apri «Turni» e scegli mese e struttura.", "Consulta la tua fascia giorno per giorno.", "Se vedi un errore, segnalalo al responsabile."] },
  { titolo: "Presenze", icon: Activity, img: IMG("presenze"),
    come: "La registrazione delle presenze del personale in turno. Alimenta i conteggi del personale.",
    steps: ["Apri «Presenze».", "Registra la tua presenza secondo le indicazioni della struttura.", "L'operatore non manager vede solo il proprio turno."] },
  { titolo: "Consegne", icon: Bell, img: IMG("consegne"),
    come: "Il passaggio di consegne tra un turno e l'altro: la comunicazione scritta per chi arriva dopo di te.",
    steps: ["Apri «Consegne».", "Leggi le consegne lasciate dal turno precedente.", "Scrivi la tua comunicazione e salva: resta visibile al turno successivo."] },
  { titolo: "Terapie", icon: Pill, img: IMG("terapie"),
    come: "Un box per ogni ospite con la terapia quotidiana e quella al bisogno, modificabili al volo. Da qui spunti le somministrazioni.",
    steps: ["Apri «Terapie» e scegli la struttura.", "Segui la terapia indicata per ciascun ospite.", "Spunta la somministrazione quando eseguita; usa «Storico» per il pregresso."] },
  { titolo: "Alvo intestinale", icon: ClipboardList, img: IMG("alvo"),
    come: "Il registro giornaliero dell'evacuazione degli ospiti, per monitorarne la regolarità nel tempo.",
    steps: ["Apri «Alvo intestinale».", "Segna per ogni ospite l'esito della giornata.", "Consulta lo storico per la regolarità."] },
  { titolo: "Pulizie", icon: Sparkles, img: IMG("pulizie"),
    come: "La checklist giornaliera di pulizia divisa per aree/stanze, con metodo (lavare, spazzare, disinfettare) e frequenza. Include la pianificazione settimanale.",
    steps: ["Apri «Pulizie» e scegli la struttura e la data.", "Spunta gli ambienti man mano che li pulisci.", "Nel tab «Pianificazione settimana» vedi cosa è dovuto ogni giorno e a chi è assegnato."] },
  { titolo: "Ospedale", icon: Hospital, img: IMG("ospedale"),
    come: "La gestione degli ospiti temporaneamente in ospedale: uscita verso l'ospedale e rientro, con effetto sui conteggi di presenza.",
    steps: ["Apri «Ospedale».", "Registra l'uscita dell'ospite verso l'ospedale.", "Registra il rientro quando torna in struttura."] },
  { titolo: "Note", icon: StickyNote, img: IMG("note"),
    come: "Le tue note personali (private): puoi anche dettarle a voce e vengono trascritte automaticamente. Utili da copiare, ad esempio nelle consegne.",
    steps: ["Apri «Note».", "Scrivi o registra una nota vocale (viene trascritta).", "Copia una nota per incollarla dove serve; elimina quando non serve più."] },
];

const catAdmin = [
  { titolo: "Ricavi − Costi (Dashboard di gestione)", icon: LayoutDashboard, img: IMG("controllo"),
    come: "Il controllo di gestione mensile: per ogni mese ricavi, spese, personale e risultato reale di cassa. È la base dei numeri gestionali di tutta l'app.",
    steps: ["Apri «Ricavi - Costi» e scegli la struttura (o «Tutte»).", "Consulta il risultato mese per mese.", "Clicca un mese per il dettaglio con ricavi, personale e spese per fornitore."] },
  { titolo: "Spese (F24 e trattamento fiscale)", icon: Wallet, img: IMG("spese"),
    come: "Foglio tipo Excel con le spese del mese per categoria (vitto, utenze, affitto, varie), più due box dedicati: IVA/ritenuta (F24) e trattamento fiscale.",
    steps: ["Apri «Spese» e scegli il mese.", "Compila le voci nelle categorie.", "Nel box F24 metti le spese con IVA/ritenuta da versare.", "Nel box fiscale segna «deducibilità ridotta» o «bene da ammortizzare»; la stella marca una spesa ricorrente."] },
  { titolo: "Ammortamenti", icon: TrendingDown, img: IMG("ammortamenti"),
    come: "La gestione dei cespiti: ogni bene è ripartito in quote annue in base all'aliquota. I beni capitalizzati dalle Spese compaiono qui in automatico.",
    steps: ["Apri «Ammortamenti».", "Aggiungi un bene con valore e aliquota (la categoria suggerisce l'aliquota).", "I beni «da Spese» hanno un badge dedicato."] },
  { titolo: "Mutui", icon: Landmark, img: IMG("mutui"),
    come: "Il piano di ammortamento dei mutui: importo, tasso e durata generano le rate; puoi anche caricare il PDF della banca e farlo leggere all'AI.",
    steps: ["Apri «Mutui».", "Inserisci importo, tasso e durata e premi «Genera piano» (o carica il PDF).", "Spunta le rate man mano che le paghi; il riepilogo mostra quanto manca all'estinzione."] },
  { titolo: "Risultati & Tasse", icon: Percent, img: IMG("tasse"),
    come: "La vista fiscale: imposta l'aliquota e per ogni mese vedi utile ante imposte, tasse, utile netto e cassa effettiva. Esporta il report per il commercialista.",
    steps: ["Apri «Risultati & Tasse» e imposta l'aliquota.", "Consulta i box con la spiegazione del calcolo.", "Clicca un mese per il dettaglio.", "Scarica il «Report commercialista (PDF)»."] },
  { titolo: "Banche", icon: Building2, img: IMG("banche"),
    come: "L'elenco dei conti correnti con banca, numero, IBAN e saldo. I saldi confluiscono nella Dashboard amministrativa.",
    steps: ["Apri «Banche».", "Aggiungi i conti con banca, numero, IBAN e saldo.", "La data si aggiorna da sola quando cambi un saldo."] },
  { titolo: "Buste paga", icon: Users, img: IMG("buste"),
    come: "Gli stipendi mensili del personale e i relativi F24. I totali confluiscono nel costo del personale della gestione.",
    steps: ["Apri «Buste paga».", "Genera gli operatori attivi del mese e compila gli importi.", "Puoi inviare le buste via email (con PDF allegato)."] },
  { titolo: "Dashboard amministrativa", icon: LayoutDashboard, img: IMG("dash-admin"),
    come: "Il quadro completo dell'economia: saldi banche, box economici, rette e costi, situazione fiscale, mutui e la tabella annuale fino alla cassa effettiva.",
    steps: ["Apri «Dashboard amministrativa».", "In alto scegli l'anno e controlla i saldi banche.", "Leggi i box e i grafici, poi apri il mese per la tabella completa."] },
  { titolo: "Utenti, permessi e strutture", icon: Users, img: IMG("utenti"),
    come: "La gestione degli accessi: account, operatori collegati e permessi per modulo. Da qui si reimpostano password e PIN.",
    steps: ["Apri «Utenti e permessi».", "Crea gli operatori con email, password e PIN.", "Assegna i permessi: l'operatore vedrà solo i moduli abilitati.", "Icona chiave = reimposta password e PIN; l'occhio mostra la password."] },
];

const IntroBlock = ({ b }) => {
  const Icon = b.icon || Info;
  return (
    <div className="gr-card p-5" data-testid={`guida-intro-${b.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="h-9 w-9 rounded-lg bg-primary/10 grid place-items-center"><Icon size={18} className="text-primary" /></span>
        <h3 className="font-head font-bold text-base md:text-lg">{b.titolo}</h3>
      </div>
      {b.testo && <p className="text-sm text-muted-foreground">{b.testo}</p>}
      {b.img && (
        <figure className="mt-3">
          <div className="rounded-lg overflow-hidden border max-h-[360px] overflow-y-auto bg-secondary/20">
            <img src={b.img} alt={b.titolo} className="w-full block" loading="lazy" />
          </div>
          {b.imgCaption && <figcaption className="text-xs text-muted-foreground mt-1.5 italic">{b.imgCaption}</figcaption>}
        </figure>
      )}
      {b.punti && (
        <ul className="mt-3 space-y-1.5">
          {b.punti.map((p, i) => (
            <li key={i} className="flex items-start gap-2 text-sm"><ChevronRight size={15} className="text-primary mt-0.5 shrink-0" /><span>{p}</span></li>
          ))}
        </ul>
      )}
      {b.steps && (
        <ol className="mt-3 space-y-1.5">
          {b.steps.map((s, i) => (
            <li key={i} className="flex items-start gap-2 text-sm"><span className="h-5 w-5 shrink-0 rounded-full bg-primary/10 text-primary text-[11px] font-bold grid place-items-center">{i + 1}</span><span>{s}</span></li>
          ))}
        </ol>
      )}
    </div>
  );
};

const CategoryCard = ({ c }) => {
  const Icon = c.icon || BookOpen;
  return (
    <div className="gr-card p-5" data-testid={`guida-cat-${c.titolo}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="h-9 w-9 rounded-lg bg-primary/10 grid place-items-center"><Icon size={18} className="text-primary" /></span>
        <h3 className="font-head font-bold text-base md:text-lg">{c.titolo}</h3>
      </div>
      <p className="text-sm text-muted-foreground">{c.come}</p>
      <figure className="mt-3">
        <div className="rounded-lg overflow-hidden border max-h-[380px] overflow-y-auto bg-secondary/20">
          <img src={c.img} alt={c.titolo} className="w-full block" loading="lazy" />
        </div>
        <figcaption className="text-xs text-muted-foreground mt-1.5 italic">Schermata reale del modulo «{c.titolo}».</figcaption>
      </figure>
      <ol className="mt-3 space-y-1.5">
        {(c.steps || []).map((s, i) => (
          <li key={i} className="flex items-start gap-2 text-sm"><ChevronRight size={15} className="text-primary mt-0.5 shrink-0" /><span>{s}</span></li>
        ))}
      </ol>
    </div>
  );
};

export default function Guida() {
  const { user, companyLogo } = useAuth();
  const isAdmin = ["admin", "super_admin", "responsabile"].includes(user?.role);
  const [tab, setTab] = useState("operatore");
  const intro = tab === "admin" ? introAdmin : introOperatore;
  const categorie = tab === "admin" ? catAdmin : catOperatore;
  const titolo = tab === "admin" ? "Guida amministrativa" : "Guida operatori";
  const link = `${window.location.origin}/guida`;

  // Costruisce i gruppi testuali per il PDF (intro + categorie)
  const gruppiPdf = [
    ...intro.map((b) => ({ titolo: b.titolo, voci: [{ h: "In breve", d: b.testo, steps: b.punti || b.steps || [] }] })),
    ...categorie.map((c) => ({ titolo: c.titolo, voci: [{ h: "Come funziona", d: c.come, steps: c.steps || [] }] })),
  ];

  const scaricaPdf = () => { try { exportGuida({ title: titolo, sottotitolo: "Gestione Residenze — guida all'uso", gruppi: gruppiPdf, logo: companyLogo }); toast.success("Guida PDF generata"); } catch { toast.error("Errore nella generazione del PDF"); } };
  const scaricaPdfIllustrato = async () => {
    const sezioni = [
      ...intro.map((b) => ({ titolo: b.titolo, testo: b.testo, steps: b.punti || b.steps || [], img: b.img })),
      ...categorie.map((c) => ({ titolo: c.titolo, testo: c.come, steps: c.steps || [], img: c.img })),
    ];
    const t = toast.loading("Preparo il PDF illustrato…");
    try {
      await exportGuidaIllustrata({ title: titolo, sottotitolo: "Gestione Residenze — guida illustrata per la bacheca", sezioni, logo: companyLogo });
      toast.success("PDF illustrato generato", { id: t });
    } catch { toast.error("Errore nella generazione del PDF", { id: t }); }
  };
  const copiaLink = async () => { try { await navigator.clipboard.writeText(link); toast.success("Link copiato"); } catch { toast.error("Impossibile copiare"); } };
  const condividiWhatsapp = () => { const msg = `Ciao! Ecco la guida all'uso del gestionale (${titolo}): ${link}`; window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank"); };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="guida-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-head font-bold flex items-center gap-2"><BookOpen size={22} className="text-primary" /> Guida all'uso</h1>
          <p className="text-sm text-muted-foreground">Istruzioni passo-passo con le schermate reali dell'app. Scarica il PDF o condividi il link.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={copiaLink} data-testid="guida-copy-link"><Copy size={15} className="mr-1.5" /> Copia link</Button>
          <Button variant="outline" onClick={condividiWhatsapp} data-testid="guida-whatsapp"><Share2 size={15} className="mr-1.5" /> WhatsApp</Button>
          <Button variant="outline" onClick={scaricaPdfIllustrato} className="font-semibold" data-testid="guida-pdf-illustrato"><FileDown size={15} className="mr-1.5" /> PDF illustrato (bacheca)</Button>
          <Button onClick={scaricaPdf} className="font-semibold" data-testid="guida-pdf"><FileDown size={15} className="mr-1.5" /> Scarica PDF</Button>
        </div>
      </div>

      {isAdmin && (
        <div className="inline-flex rounded-lg border p-1 bg-secondary/40" data-testid="guida-tabs">
          <button onClick={() => setTab("operatore")} className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${tab === "operatore" ? "bg-white shadow text-primary" : "text-muted-foreground"}`} data-testid="guida-tab-operatore">Operatori</button>
          <button onClick={() => setTab("admin")} className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${tab === "admin" ? "bg-white shadow text-primary" : "text-muted-foreground"}`} data-testid="guida-tab-admin">Amministrativa</button>
        </div>
      )}

      {/* Introduzione strutturata */}
      <section data-testid="guida-introduzione">
        <div className="flex items-center gap-2 mb-3">
          <HeartPulse size={18} className="text-primary" />
          <h2 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Per iniziare</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4 items-start">
          {intro.map((b) => <IntroBlock key={b.id} b={b} />)}
        </div>
      </section>

      {/* Guida per categoria */}
      <section data-testid="guida-categorie">
        <div className="flex items-center gap-2 mb-3 mt-2">
          <ClipboardList size={18} className="text-primary" />
          <h2 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Guida per categoria (con schermate)</h2>
        </div>
        <div className="grid lg:grid-cols-2 gap-4 items-start">
          {categorie.map((c, i) => <CategoryCard key={i} c={c} />)}
        </div>
      </section>
    </div>
  );
}
